import React, { useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';

import {

  equipmentInstanceOrigin,

  equipmentInstanceToLayoutElement,

  equipmentProvenance,

  isEquipmentOverlayVisible,

  polygonBounds,

} from '../../utils/equipmentGeometry';

import {

  EQUIPMENT_3D_DEPTH,

  defaultElementDepth,

  resolveEquipmentRenderHeight,

} from '../../utils/layout3d';

import { resolveEquipment3DPortFacing } from './amhs/Equipment/equipmentFacing3d';

import { resolveKitDockLayout } from './amhs/Equipment/equipmentPortOwnership';

import { selectInstance } from '../../features/equipment/equipmentSlice';

import { useSimulationInventoryCounts } from '../../hooks/useSimulationFrames';

import FabAmhsFloorLayer3D from './FabAmhsFloorLayer3D';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';

import TransferAnimationLayer3D from './TransferAnimationLayer3D';



/**

 * Renders overlay equipment in 3D via full procedural kits + transfer overlays.

 */

const EquipmentLayer3D: React.FC = () => {

  const dispatch = useAppDispatch();

  const order = useAppSelector((s) => s.equipment.instanceOrder);

  const instances = useAppSelector((s) => s.equipment.instances);

  const hiddenProcesses = useAppSelector((s) => s.equipment.hiddenProcesses);

  const overlayVisible = useAppSelector((s) => s.equipment.visible);

  const dxfLayers = useAppSelector((s) => s.floorplan.layers);

  const hiddenEntityTypes = useAppSelector((s) => s.floorplan.hiddenEntityTypes);

  const layoutElements = useAppSelector((s) => s.layout.elements);

  const templates = useAppSelector((s) => s.equipment.templates);

  const { stockerFoups } = useSimulationInventoryCounts();



  const descriptors = useMemo(() => {

    const out: EquipmentRenderDescriptor[] = [];

    for (const id of order) {

      const inst = instances[id];

      if (!inst || !inst.polygon || inst.polygon.length < 2) continue;

      if (equipmentProvenance(inst) === 'dxf') continue;
      if (layoutElements[id]) continue;

      if (

        !isEquipmentOverlayVisible(inst, {

          overlayVisible,

          dxfLayers,

          hiddenEntityTypes,

          hiddenProcesses,

          layoutElements,

        })

      ) {

        continue;

      }



      const bounds = polygonBounds(inst.polygon);

      const width = Math.max(0.5, bounds.maxX - bounds.minX);

      const height = Math.max(0.5, bounds.maxY - bounds.minY);

      const origin = equipmentInstanceOrigin(inst);

      const kind = inst.objectType === 'stocker' ? 'stocker' : 'equipment';

      const depth = resolveEquipmentRenderHeight(

        kind,

        width,

        height,

        kind === 'stocker' ? defaultElementDepth('stocker') : EQUIPMENT_3D_DEPTH

      );

      const template = inst.templateId ? templates[inst.templateId] ?? null : null;

      const simElement = equipmentInstanceToLayoutElement(inst, template);

      const facing = resolveEquipment3DPortFacing(simElement);

      const docks = kind === 'equipment' ? resolveKitDockLayout(simElement, instances) : null;



      out.push({

        id,

        originX: origin.x,

        originY: origin.y,

        width,

        height,

        depth,

        rotationDeg: inst.rotation || 0,

        kind,

        facingRotationY: facing.rotationY,

        swapsFootprintAxes: facing.swapsFootprintAxes,

        loadPortCount: docks?.loadPortCount,

        portFractions: docks?.portFractions,

        storedFoupsCount: kind === 'stocker' ? (stockerFoups[id] ?? 0) : 0,

      });

    }

    return out;

  }, [

    order,

    instances,

    hiddenProcesses,

    overlayVisible,

    dxfLayers,

    hiddenEntityTypes,

    layoutElements,

    templates,

    stockerFoups,

  ]);



  const equipmentLayoutMeta = useMemo(() => {
    const meta: Record<string, ReturnType<typeof equipmentInstanceToLayoutElement>> = {};
    for (const descriptor of descriptors) {
      const inst = instances[descriptor.id];
      if (!inst) continue;
      const template = inst.templateId ? templates[inst.templateId] ?? null : null;
      meta[descriptor.id] = equipmentInstanceToLayoutElement(inst, template);
    }
    return meta;
  }, [descriptors, instances, templates]);

  if (descriptors.length === 0) return null;

  return (
    <group>
      <FabAmhsFloorLayer3D
        descriptors={descriptors}
        layoutMeta={equipmentLayoutMeta}
        onSelect={(id) => dispatch(selectInstance(id))}
      />

      <TransferAnimationLayer3D descriptors={descriptors} layoutMeta={equipmentLayoutMeta} />

    </group>
  );

};



export default React.memo(EquipmentLayer3D);

